import { useQuery } from '@powersync/react';
import Constants from 'expo-constants';
import { View } from 'react-native';
import Animated, { useAnimatedRef } from 'react-native-reanimated';

import { SettingsGroup } from '@/components/domain/SettingsGroup';
import { SyncStatusArea } from '@/components/domain/syncStatus/SyncStatusArea';
import { SettingsRow } from '@/components/domain/SettingsRow';
import { Badge } from '@/components/ui/Badge';
import { BoundedScrollView } from '@/components/ui/BoundedScrollView';
import { Button } from '@/components/ui/Button';
import { ListRow } from '@/components/ui/ListRow';
import { NavBar } from '@/components/ui/NavBar';
import { Screen } from '@/components/ui/Screen';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Surface } from '@/components/ui/Surface';
import { Text } from '@/components/ui/Text';
import { useAppearance, type Appearance } from '@/lib/appearance';
import { useAuth } from '@/lib/auth';
import { BILLING_ENABLED, useEntitlement } from '@/lib/billing';
import { emailLocalPart } from '@/lib/format';
import { useGoBack } from '@/lib/navigation';

const APPEARANCE_OPTIONS: { value: Appearance; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

export default function SettingsScreen() {
  const goBack = useGoBack('/');
  const syncScrollRef = useAnimatedRef<Animated.ScrollView>();
  const { session, signOut } = useAuth();
  const { appearance, setAppearance } = useAppearance();
  const { isPro, restore } = useEntitlement();
  const { data: profiles } = useQuery<{ display_name: string | null }>(
    'SELECT display_name FROM profiles WHERE id = ?',
    [session?.user.id ?? ''],
  );
  const email = session?.user.email ?? '';
  const displayName = profiles[0]?.display_name ?? emailLocalPart(email) ?? 'You';
  const version = Constants.expoConfig?.version ?? '1.0.0';

  return (
    <Screen edges={['top']}>
      <NavBar onBack={goBack} title="Settings" />

      <SyncStatusArea scrollRef={syncScrollRef}>
        <BoundedScrollView
          scrollRef={syncScrollRef}
          alwaysBounceVertical
          contentContainerClassName="gap-6 px-5 pb-10 pt-2"
        >
          <Surface className="overflow-hidden">
            <ListRow title={displayName} subtitle={email} href="/settings/account" />
          </Surface>

          <View className="gap-3">
            <SectionHeader label="Appearance" />
            <SegmentedControl
              options={APPEARANCE_OPTIONS}
              value={appearance}
              onChange={setAppearance}
            />
          </View>

          {BILLING_ENABLED ? (
            <SettingsGroup title="Subscription">
              <SettingsRow
                label="Plan"
                chevron={false}
                right={
                  isPro ? (
                    <Badge label="Pro" tone="pro" />
                  ) : (
                    <Button size="sm" variant="outline" label="Upgrade" href="/paywall" />
                  )
                }
              />
              <SettingsRow
                label="Restore purchase"
                onPress={async () => {
                  try {
                    await restore();
                  } catch (e) {
                    alert((e as Error).message);
                  }
                }}
              />
            </SettingsGroup>
          ) : null}

          <SettingsGroup title="Support">
            <SettingsRow label="Help & support" onPress={() => {}} />
            <SettingsRow label="Terms of Service" href="/legal/terms" />
            <SettingsRow label="Privacy Policy" href="/legal/privacy" />
          </SettingsGroup>

          <Button variant="ghost" label="Sign out" onPress={signOut} />

          <Text variant="accent" className="text-center text-faint">
            Remove Before Flight · v{version}
          </Text>
        </BoundedScrollView>
      </SyncStatusArea>
    </Screen>
  );
}
